/**
 * Campus Admin — Super Admin only editing space for Campus Valley 360°.
 * Manages 10 pillars + 14 use cases lessons (CRUD, media upload, quiz builder).
 */
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { supabase } from '@/lib/supabase';
import {
    Loader2, Plus, Pencil, Trash2, Save, Upload, Video, Headphones,
    FileText, FileSpreadsheet, X, ChevronRight, BookOpen, Layers,
    GripVertical, AlertTriangle, Youtube,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CampusAdmin({ auth }) {
    const [pillars, setPillars] = useState([]);
    const [useCases, setUseCases] = useState([]);
    const [stats, setStats] = useState(null);
    const [lessonStats, setLessonStats] = useState([]);
    const [statsOpen, setStatsOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [selectedParent, setSelectedParent] = useState(null); // {type, id, name}
    const [lessons, setLessons] = useState([]);
    const [editingLesson, setEditingLesson] = useState(null); // lesson object or null
    const [editingPillar, setEditingPillar] = useState(null);
    const [busy, setBusy] = useState(false);

    const reload = async () => {
        if (!auth) return;
        setLoading(true);
        try {
            const [pr, ur, st, ls] = await Promise.all([
                axios.get(`${API}/campus/pillars`, auth),
                axios.get(`${API}/campus/use-cases`, auth),
                axios.get(`${API}/super-admin/campus/stats`, auth),
                axios.get(`${API}/super-admin/campus/lesson-stats`, auth).catch(() => ({ data: { items: [] } })),
            ]);
            setPillars(pr.data.items || []);
            setUseCases(ur.data.items || []);
            setStats(st.data);
            setLessonStats(ls.data.items || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { reload(); /* eslint-disable-next-line */ }, [auth]);

    const loadLessons = async (parentType, parentId) => {
        try {
            // We use direct supabase query (Super Admin has RLS access)
            const { data, error } = await supabase.from('campus_lessons')
                .select('*')
                .eq('parent_type', parentType)
                .eq('parent_id', parentId)
                .order('order_index', { ascending: true });
            if (error) throw error;
            setLessons(data || []);
        } catch (e) {
            toast.error(e.message);
        }
    };

    const onSelectParent = async (type, id, name) => {
        setSelectedParent({ type, id, name });
        await loadLessons(type, id);
    };

    const savePillar = async () => {
        if (!editingPillar) return;
        setBusy(true);
        try {
            if (editingPillar.id) {
                await axios.patch(`${API}/super-admin/campus/pillars/${editingPillar.id}`,
                    editingPillar, auth);
            } else {
                await axios.post(`${API}/super-admin/campus/pillars`, editingPillar, auth);
            }
            toast.success('Pilier enregistré');
            setEditingPillar(null);
            await reload();
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        setBusy(false);
    };

    const deletePillar = async (p) => {
        if (!window.confirm(`Supprimer le pilier "${p.title_fr}" et toutes ses leçons ?`)) return;
        try {
            await axios.delete(`${API}/super-admin/campus/pillars/${p.id}`, auth);
            toast.success('Pilier supprimé');
            await reload();
            if (selectedParent?.id === p.id) { setSelectedParent(null); setLessons([]); }
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    };

    const saveLesson = async () => {
        if (!editingLesson) return;
        const required = ['title_fr', 'title_en', 'slug'];
        for (const f of required) {
            if (!editingLesson[f]?.trim?.()) { toast.error(`Champ requis : ${f}`); return; }
        }
        setBusy(true);
        try {
            const body = {
                ...editingLesson,
                parent_type: selectedParent.type,
                parent_id: selectedParent.id,
                quiz: editingLesson.quiz || [],
            };
            if (editingLesson.id) {
                await axios.patch(`${API}/super-admin/campus/lessons/${editingLesson.id}`,
                    body, auth);
            } else {
                await axios.post(`${API}/super-admin/campus/lessons`, body, auth);
            }
            toast.success('Leçon enregistrée');
            setEditingLesson(null);
            await loadLessons(selectedParent.type, selectedParent.id);
            await reload();
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
        setBusy(false);
    };

    const deleteLesson = async (l) => {
        if (!window.confirm(`Supprimer la leçon "${l.title_fr}" ?`)) return;
        try {
            await axios.delete(`${API}/super-admin/campus/lessons/${l.id}`, auth);
            toast.success('Leçon supprimée');
            await loadLessons(selectedParent.type, selectedParent.id);
            await reload();
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    };

    // Direct upload to Supabase Storage via the JS client (admin session = RLS Super Admin pass)
    const uploadMedia = async (file, kind) => {
        if (!file) return null;
        try {
            const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
            const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 80);
            const path = `${selectedParent.type}/${selectedParent.id}/${kind}/${ts}-${safeName}`;
            const { error } = await supabase.storage.from('campus').upload(path, file, {
                cacheControl: '3600', upsert: false, contentType: file.type,
            });
            if (error) throw error;
            const { data } = supabase.storage.from('campus').getPublicUrl(path);
            return data.publicUrl;
        } catch (e) {
            toast.error(`Upload échoué : ${e.message}`);
            return null;
        }
    };

    const exportStatsCsv = () => {
        const header = ['Leçon', 'Type', 'Démarrées', 'Complétées', 'Taux de complétion (%)', 'Quiz moyen (%)', 'Recommandations'];
        const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
        const rows = [...lessonStats]
            .sort((a, b) => (a.completion_rate ?? 101) - (b.completion_rate ?? 101))
            .map(s => [s.title, s.parent_type === 'pillar' ? 'Tronc commun' : 'Secteur', s.started, s.completed,
                s.completion_rate ?? '', s.avg_quiz ?? '', s.recommendations || 0].map(esc).join(';'));
        const csv = '\ufeff' + [header.map(esc).join(';'), ...rows].join('\n');
        const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `campus-completion-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Export CSV téléchargé');
    };

    if (loading) return <div className="flex items-center justify-center py-10"><Loader2 className="animate-spin" /></div>;

    return (
        <div className="space-y-5" data-testid="sa-campus-tab">
            {/* Top stats */}
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <StatBox label="Piliers" value={stats.pillars_count} icon={Layers} />
                    <StatBox label="Leçons" value={stats.lessons_count} icon={BookOpen} sub={`${stats.lessons_pillar} tronc · ${stats.lessons_use_case} secteurs`} />
                    <StatBox label="Apprenants actifs" value={stats.active_learners} />
                    <StatBox label="Validations" value={stats.completions} />
                </div>
            )}

            {/* Taux de complétion par leçon */}
            {lessonStats.length > 0 && (
                <div className="card-soft" data-testid="sa-campus-lesson-stats">
                    <div className="w-full flex items-center justify-between px-5 py-4 gap-3">
                        <button type="button" onClick={() => setStatsOpen(o => !o)} className="flex-1 text-left"
                            data-testid="sa-campus-lesson-stats-toggle">
                            <h3 className="font-heading text-lg">Taux de complétion par leçon</h3>
                            <p className="text-xs text-muted-foreground mt-0.5">Repérez les contenus où les apprenants décrochent (triés du plus faible taux au plus fort).</p>
                        </button>
                        <Button size="sm" variant="outline" onClick={exportStatsCsv} data-testid="sa-campus-stats-export">
                            <FileSpreadsheet size={13} className="mr-1.5" /> Export CSV
                        </Button>
                        <button type="button" onClick={() => setStatsOpen(o => !o)} aria-label="Déplier">
                            <ChevronRight size={16} className={`transition-transform ${statsOpen ? 'rotate-90' : ''}`} />
                        </button>
                    </div>
                    {statsOpen && (
                        <div className="overflow-x-auto border-t border-border">
                            <table className="w-full text-sm">
                                <thead className="bg-surface-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
                                    <tr>
                                        <th className="px-5 py-2.5">Leçon</th>
                                        <th className="px-5 py-2.5">Type</th>
                                        <th className="px-5 py-2.5">Démarrées</th>
                                        <th className="px-5 py-2.5">Complétées</th>
                                        <th className="px-5 py-2.5 w-56">Taux de complétion</th>
                                        <th className="px-5 py-2.5">Quiz moyen</th>
                                        <th className="px-5 py-2.5">Recomm.</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border">
                                    {[...lessonStats].sort((a, b) => (a.completion_rate ?? 101) - (b.completion_rate ?? 101)).map(s => (
                                        <tr key={s.id} data-testid={`sa-lesson-stat-${s.id}`}>
                                            <td className="px-5 py-2.5 font-medium max-w-[280px] truncate">{s.title}</td>
                                            <td className="px-5 py-2.5 text-xs text-muted-foreground">{s.parent_type === 'pillar' ? 'Tronc commun' : 'Secteur'}</td>
                                            <td className="px-5 py-2.5 tabular-nums">{s.started}</td>
                                            <td className="px-5 py-2.5 tabular-nums">{s.completed}</td>
                                            <td className="px-5 py-2.5">
                                                {s.completion_rate === null ? (
                                                    <span className="text-xs text-muted-foreground">Aucun apprenant</span>
                                                ) : (
                                                    <div className="flex items-center gap-2">
                                                        <div className="h-1.5 flex-1 rounded-full bg-border overflow-hidden">
                                                            <div className={`h-full ${s.completion_rate < 40 ? 'bg-destructive' : s.completion_rate < 70 ? 'bg-amber-500' : 'bg-secondary'}`}
                                                                style={{ width: `${s.completion_rate}%` }} />
                                                        </div>
                                                        <span className={`text-xs font-medium tabular-nums w-10 text-right ${s.completion_rate < 40 ? 'text-destructive' : s.completion_rate < 70 ? 'text-amber-600' : 'text-secondary'}`}>{s.completion_rate}%</span>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-5 py-2.5 tabular-nums text-xs">{s.avg_quiz !== null ? `${s.avg_quiz}%` : '—'}</td>
                                            <td className="px-5 py-2.5 tabular-nums text-xs">{s.recommendations || '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            <div className="grid lg:grid-cols-12 gap-5">
                {/* Left rail : parent list */}
                <div className="lg:col-span-4 space-y-5">
                    <div className="card-soft p-4">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="font-heading text-lg">10 Piliers</h3>
                            <Button size="sm" onClick={() => setEditingPillar({ order_index: pillars.length + 1, title_fr: '', title_en: '', slug: '' })}
                                    className="bg-primary hover:bg-primary-hover" data-testid="sa-campus-pillar-new">
                                <Plus size={13} className="mr-1" /> Pilier
                            </Button>
                        </div>
                        <ul className="space-y-1">
                            {pillars.map(p => (
                                <li key={p.id}
                                    className={`group flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${selectedParent?.id === p.id ? 'bg-primary-light text-primary' : 'hover:bg-surface-muted'}`}
                                    onClick={() => onSelectParent('pillar', p.id, p.title_fr)}
                                    data-testid={`sa-campus-pillar-${p.slug}`}>
                                    <span className="font-mono text-[10px] text-muted-foreground">P{p.order_index}</span>
                                    <span className="flex-1 text-sm truncate">{p.title_fr}</span>
                                    <button onClick={(e) => { e.stopPropagation(); setEditingPillar({ ...p }); }} className="opacity-0 group-hover:opacity-100"><Pencil size={11} /></button>
                                    <button onClick={(e) => { e.stopPropagation(); deletePillar(p); }} className="opacity-0 group-hover:opacity-100 text-red-500"><Trash2 size={11} /></button>
                                    <ChevronRight size={11} className="text-muted-foreground" />
                                </li>
                            ))}
                            {pillars.length === 0 && (
                                <li className="text-xs text-muted-foreground italic px-3 py-2">Aucun pilier. Cliquez sur "+ Pilier".</li>
                            )}
                        </ul>
                    </div>

                    <div className="card-soft p-4">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="font-heading text-lg">14 Cas d'usage</h3>
                            <span className="text-xs text-muted-foreground italic">Pré-existants</span>
                        </div>
                        <ul className="space-y-1 max-h-80 overflow-y-auto">
                            {useCases.map(uc => (
                                <li key={uc.id}
                                    className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${selectedParent?.id === uc.id ? 'bg-secondary/15 text-secondary' : 'hover:bg-surface-muted'}`}
                                    onClick={() => onSelectParent('use_case', uc.id, uc.title_fr)}
                                    data-testid={`sa-campus-usecase-${uc.slug}`}>
                                    <span className="font-mono text-[10px] text-muted-foreground truncate">{uc.sector?.slice(0, 14)}</span>
                                    <span className="flex-1 text-sm truncate">{uc.title_fr}</span>
                                    <ChevronRight size={11} className="text-muted-foreground" />
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* Right pane : lessons of selected parent */}
                <div className="lg:col-span-8">
                    {!selectedParent ? (
                        <div className="card-soft p-12 text-center text-foreground/50">
                            <Layers size={40} className="mx-auto mb-3 opacity-40" />
                            Sélectionnez un pilier ou un cas d'usage pour éditer ses leçons.
                        </div>
                    ) : (
                        <div className="card-soft p-5">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <div className="text-xs uppercase tracking-wider text-muted-foreground">
                                        {selectedParent.type === 'pillar' ? 'Pilier' : 'Cas d\'usage'}
                                    </div>
                                    <h3 className="font-heading text-xl">{selectedParent.name}</h3>
                                </div>
                                <Button onClick={() => setEditingLesson(newEmptyLesson(lessons.length))}
                                        className="bg-primary hover:bg-primary-hover" data-testid="sa-campus-lesson-new">
                                    <Plus size={14} className="mr-1.5" /> Nouvelle leçon
                                </Button>
                            </div>
                            {lessons.length === 0 ? (
                                <div className="text-center py-8 text-foreground/50 text-sm">
                                    Aucune leçon. Ajoutez du contenu multimédia.
                                </div>
                            ) : (
                                <ul className="space-y-2" data-testid="sa-campus-lesson-list">
                                    {lessons.map((l, idx) => (
                                        <li key={l.id} className="flex items-center gap-3 p-3 rounded-lg border border-border hover:border-primary/40 transition-colors">
                                            <GripVertical size={14} className="text-muted-foreground" />
                                            <span className="font-mono text-xs text-muted-foreground">L{idx + 1}</span>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-medium truncate">{l.title_fr}</div>
                                                <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">
                                                    {l.video_url && <span className="text-primary"><Video size={10} className="inline mr-0.5" />Vidéo</span>}
                                                    {l.audio_url && <span className="text-secondary"><Headphones size={10} className="inline mr-0.5" />Audio</span>}
                                                    {l.pdf_url && <span><FileText size={10} className="inline mr-0.5" />PDF</span>}
                                                    {l.excel_url && <span className="text-emerald-600"><FileSpreadsheet size={10} className="inline mr-0.5" />Excel</span>}
                                                    {(l.quiz || []).length > 0 && <span className="text-amber-600">{l.quiz.length} Q</span>}
                                                    {!l.is_published && <span className="text-red-500">Brouillon</span>}
                                                </div>
                                            </div>
                                            <Button size="sm" variant="ghost" onClick={() => setEditingLesson({ ...l })}><Pencil size={12} /></Button>
                                            <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteLesson(l)}><Trash2 size={12} /></Button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Pillar editor dialog */}
            <Dialog open={!!editingPillar} onOpenChange={(o) => !o && setEditingPillar(null)}>
                {editingPillar && (
                    <DialogContent className="max-w-2xl">
                        <DialogHeader>
                            <DialogTitle className="font-heading text-2xl">{editingPillar.id ? 'Modifier' : 'Nouveau'} Pilier</DialogTitle>
                            <DialogDescription>Pilier du tronc commun (Core Framework).</DialogDescription>
                        </DialogHeader>
                        <div className="grid md:grid-cols-2 gap-3">
                            <Field label="Slug *"><Input value={editingPillar.slug || ''} onChange={(e) => setEditingPillar({ ...editingPillar, slug: e.target.value })} placeholder="p1-cadrage" /></Field>
                            <Field label="Ordre"><Input type="number" value={editingPillar.order_index || 0} onChange={(e) => setEditingPillar({ ...editingPillar, order_index: Number(e.target.value) })} /></Field>
                            <Field label="Titre FR *"><Input value={editingPillar.title_fr || ''} onChange={(e) => setEditingPillar({ ...editingPillar, title_fr: e.target.value })} /></Field>
                            <Field label="Titre EN *"><Input value={editingPillar.title_en || ''} onChange={(e) => setEditingPillar({ ...editingPillar, title_en: e.target.value })} /></Field>
                            <Field label="Résumé FR"><Textarea rows={2} value={editingPillar.summary_fr || ''} onChange={(e) => setEditingPillar({ ...editingPillar, summary_fr: e.target.value })} /></Field>
                            <Field label="Résumé EN"><Textarea rows={2} value={editingPillar.summary_en || ''} onChange={(e) => setEditingPillar({ ...editingPillar, summary_en: e.target.value })} /></Field>
                            <Field label="Objectifs FR"><Textarea rows={2} value={editingPillar.objectives_fr || ''} onChange={(e) => setEditingPillar({ ...editingPillar, objectives_fr: e.target.value })} /></Field>
                            <Field label="Objectifs EN"><Textarea rows={2} value={editingPillar.objectives_en || ''} onChange={(e) => setEditingPillar({ ...editingPillar, objectives_en: e.target.value })} /></Field>
                            <Field label="Livrables FR"><Textarea rows={2} value={editingPillar.deliverables_fr || ''} onChange={(e) => setEditingPillar({ ...editingPillar, deliverables_fr: e.target.value })} /></Field>
                            <Field label="Livrables EN"><Textarea rows={2} value={editingPillar.deliverables_en || ''} onChange={(e) => setEditingPillar({ ...editingPillar, deliverables_en: e.target.value })} /></Field>
                            <Field label="Icône (lucide)"><Input value={editingPillar.icon || 'Layers'} onChange={(e) => setEditingPillar({ ...editingPillar, icon: e.target.value })} placeholder="Compass" /></Field>
                            <Field label="Couleur HEX"><Input value={editingPillar.color_hex || '#C8553D'} onChange={(e) => setEditingPillar({ ...editingPillar, color_hex: e.target.value })} placeholder="#C8553D" /></Field>
                        </div>
                        <DialogFooter>
                            <Button variant="ghost" onClick={() => setEditingPillar(null)}>Annuler</Button>
                            <Button onClick={savePillar} disabled={busy} className="bg-primary hover:bg-primary-hover" data-testid="sa-campus-pillar-save">
                                {busy ? <Loader2 size={13} className="animate-spin mr-2" /> : <Save size={13} className="mr-2" />} Enregistrer
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                )}
            </Dialog>

            {/* Lesson editor dialog */}
            <Dialog open={!!editingLesson} onOpenChange={(o) => !o && setEditingLesson(null)}>
                {editingLesson && (
                    <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="font-heading text-2xl">{editingLesson.id ? 'Modifier' : 'Nouvelle'} Leçon</DialogTitle>
                            <DialogDescription>{selectedParent?.name} · {selectedParent?.type}</DialogDescription>
                        </DialogHeader>
                        <LessonForm lesson={editingLesson} setLesson={setEditingLesson}
                                    uploadMedia={uploadMedia} />
                        <DialogFooter>
                            <Button variant="ghost" onClick={() => setEditingLesson(null)}>Annuler</Button>
                            <Button onClick={saveLesson} disabled={busy} className="bg-primary hover:bg-primary-hover" data-testid="sa-campus-lesson-save">
                                {busy ? <Loader2 size={13} className="animate-spin mr-2" /> : <Save size={13} className="mr-2" />} Enregistrer
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                )}
            </Dialog>
        </div>
    );
}

function newEmptyLesson(idx) {
    return {
        slug: `lecon-${idx + 1}`,
        order_index: idx + 1,
        title_fr: '', title_en: '',
        summary_fr: '', summary_en: '',
        body_fr: '', body_en: '',
        duration_min: 10,
        video_url: '', audio_url: '', pdf_url: '', excel_url: '',
        transcript_fr: '', transcript_en: '',
        quiz: [],
        pass_score: 70,
        is_locked_by_previous: false,
        ai_enabled: true,
        is_published: true,
    };
}

function Field({ label, children, className = '' }) {
    return (
        <div className={`space-y-1.5 ${className}`}>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
            {children}
        </div>
    );
}

function StatBox({ label, value, sub, icon: Icon }) {
    return (
        <div className="card-soft p-4">
            <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
                {Icon && <Icon size={14} className="text-muted-foreground" />}
            </div>
            <div className="font-heading text-3xl tabular-nums">{value ?? 0}</div>
            {sub && <div className="text-[11px] text-muted-foreground mt-1">{sub}</div>}
        </div>
    );
}

function LessonForm({ lesson, setLesson, uploadMedia }) {
    const [uploading, setUploading] = useState(null); // kind being uploaded
    const updateField = (k, v) => setLesson(l => ({ ...l, [k]: v }));

    const handleUpload = async (kind, file) => {
        if (!file) return;
        setUploading(kind);
        const url = await uploadMedia(file, kind);
        if (url) {
            updateField(`${kind}_url`, url);
            toast.success(`${kind.toUpperCase()} uploadé`);
        }
        setUploading(null);
    };

    const addQuizQuestion = () => {
        const q = { q: '', options: ['', '', '', ''], correct: 0, explain: '' };
        updateField('quiz', [...(lesson.quiz || []), q]);
    };
    const updateQuestion = (i, patch) => {
        const next = [...(lesson.quiz || [])];
        next[i] = { ...next[i], ...patch };
        updateField('quiz', next);
    };
    const removeQuestion = (i) => updateField('quiz', (lesson.quiz || []).filter((_, k) => k !== i));

    return (
        <div className="space-y-5">
            <div className="grid md:grid-cols-2 gap-3">
                <Field label="Slug *"><Input value={lesson.slug} onChange={(e) => updateField('slug', e.target.value)} /></Field>
                <Field label="Ordre"><Input type="number" value={lesson.order_index} onChange={(e) => updateField('order_index', Number(e.target.value))} /></Field>
                <Field label="Titre FR *"><Input value={lesson.title_fr} onChange={(e) => updateField('title_fr', e.target.value)} /></Field>
                <Field label="Titre EN *"><Input value={lesson.title_en} onChange={(e) => updateField('title_en', e.target.value)} /></Field>
                <Field label="Résumé FR"><Textarea rows={2} value={lesson.summary_fr || ''} onChange={(e) => updateField('summary_fr', e.target.value)} /></Field>
                <Field label="Résumé EN"><Textarea rows={2} value={lesson.summary_en || ''} onChange={(e) => updateField('summary_en', e.target.value)} /></Field>
                <Field label="Contenu FR (HTML)" className="md:col-span-2"><Textarea rows={4} value={lesson.body_fr || ''} onChange={(e) => updateField('body_fr', e.target.value)} placeholder="<p>...</p>" /></Field>
                <Field label="Contenu EN (HTML)" className="md:col-span-2"><Textarea rows={4} value={lesson.body_en || ''} onChange={(e) => updateField('body_en', e.target.value)} placeholder="<p>...</p>" /></Field>
                <Field label="Durée (min)"><Input type="number" value={lesson.duration_min} onChange={(e) => updateField('duration_min', Number(e.target.value))} /></Field>
                <Field label="Seuil quiz (%)"><Input type="number" value={lesson.pass_score} onChange={(e) => updateField('pass_score', Number(e.target.value))} /></Field>
            </div>

            {/* Media uploaders */}
            <div className="card-soft p-4 space-y-3">
                <div className="font-heading text-lg flex items-center gap-2"><Upload size={16} /> Médias</div>
                {['video', 'audio', 'pdf', 'excel'].map((kind) => (
                    <MediaRow key={kind} kind={kind} url={lesson[`${kind}_url`]} uploading={uploading === kind}
                              onUpload={(f) => handleUpload(kind, f)}
                              onClear={() => updateField(`${kind}_url`, '')}
                              onSetUrl={(v) => updateField(`${kind}_url`, v)} />
                ))}
            </div>

            <Field label="Transcript FR (utilisé par Campus IA)"><Textarea rows={3} value={lesson.transcript_fr || ''} onChange={(e) => updateField('transcript_fr', e.target.value)} /></Field>

            {/* Quiz builder */}
            <div className="card-soft p-4 space-y-3">
                <div className="flex items-center justify-between">
                    <div className="font-heading text-lg">Quiz</div>
                    <Button size="sm" onClick={addQuizQuestion} className="bg-primary hover:bg-primary-hover">
                        <Plus size={12} className="mr-1" /> Question
                    </Button>
                </div>
                {(lesson.quiz || []).length === 0 && (
                    <div className="text-xs text-muted-foreground italic">Aucune question. La leçon sera complétable par simple clic.</div>
                )}
                {(lesson.quiz || []).map((q, qi) => (
                    <div key={qi} className="p-3 rounded-lg border border-border space-y-2 relative">
                        <button className="absolute top-2 right-2 text-red-500" onClick={() => removeQuestion(qi)}><X size={13} /></button>
                        <Input placeholder={`Question ${qi + 1}`} value={q.q} onChange={(e) => updateQuestion(qi, { q: e.target.value })} />
                        {(q.options || ['', '', '', '']).map((opt, oi) => (
                            <div key={oi} className="flex items-center gap-2">
                                <input type="radio" checked={q.correct === oi} onChange={() => updateQuestion(qi, { correct: oi })} />
                                <Input className="h-8 text-sm" placeholder={`Option ${oi + 1}`} value={opt}
                                       onChange={(e) => updateQuestion(qi, { options: q.options.map((o, k) => k === oi ? e.target.value : o) })} />
                            </div>
                        ))}
                        <Input className="h-8 text-xs" placeholder="Explication (facultatif)" value={q.explain || ''} onChange={(e) => updateQuestion(qi, { explain: e.target.value })} />
                    </div>
                ))}
            </div>

            {/* Flags */}
            <div className="grid grid-cols-3 gap-3">
                <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={lesson.is_published} onChange={(e) => updateField('is_published', e.target.checked)} /> Publiée
                </label>
                <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={lesson.ai_enabled} onChange={(e) => updateField('ai_enabled', e.target.checked)} /> Assistant IA
                </label>
                <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={lesson.is_locked_by_previous} onChange={(e) => updateField('is_locked_by_previous', e.target.checked)} /> Verrou progressif
                </label>
            </div>
        </div>
    );
}

function MediaRow({ kind, url, uploading, onUpload, onClear, onSetUrl }) {
    const KIND_META = {
        video: { Icon: Video, label: 'Vidéo · MP4/WebM OU lien YouTube/Vimeo', accept: 'video/*', placeholder: 'https://youtu.be/… ou URL fichier MP4' },
        audio: { Icon: Headphones, label: 'Audio (MP3, WAV, OGG)', accept: 'audio/*', placeholder: 'URL ou uploadez un fichier' },
        pdf: { Icon: FileText, label: 'Document PDF', accept: 'application/pdf', placeholder: 'URL ou uploadez un fichier' },
        excel: { Icon: FileSpreadsheet, label: 'Tableau Excel (XLSX)', accept: '.xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', placeholder: 'URL ou uploadez un fichier' },
    };
    const meta = KIND_META[kind];
    const handleYouTubePrompt = () => {
        const raw = window.prompt(
            'Coller un lien YouTube ou Vimeo :\n\n' +
            '• https://youtu.be/<id>\n' +
            '• https://www.youtube.com/watch?v=<id>\n' +
            '• https://vimeo.com/<id>\n\n' +
            'L\'embed sera automatiquement détecté côté lecteur (RGPD-friendly : youtube-nocookie + dnt=1).',
            url && /(youtube|youtu\.be|vimeo)/i.test(url) ? url : ''
        );
        if (raw == null) return;
        const trimmed = raw.trim();
        if (!trimmed) return;
        const valid = /(youtu\.be\/|youtube\.com\/(?:watch|embed|shorts)|vimeo\.com\/\d+|player\.vimeo\.com\/video\/\d+)/i.test(trimmed);
        if (!valid) {
            toast.error('Lien non reconnu — vérifiez qu\'il s\'agit bien d\'une URL YouTube ou Vimeo.');
            return;
        }
        onSetUrl(trimmed);
        toast.success('Lien YouTube/Vimeo enregistré. Pensez à sauvegarder la leçon.');
    };
    return (
        <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-md bg-surface-muted flex items-center justify-center shrink-0">
                <meta.Icon size={14} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{meta.label}</div>
                <Input className="h-8 text-xs" value={url || ''} placeholder={meta.placeholder || 'URL ou uploadez un fichier'}
                       onChange={(e) => onSetUrl(e.target.value)} data-testid={`sa-campus-${kind}-url`} />
            </div>
            {kind === 'video' && (
                <button type="button" onClick={handleYouTubePrompt}
                        title="Coller un lien YouTube ou Vimeo"
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-border bg-white hover:bg-red-50 hover:border-red-200 text-xs font-medium text-red-600 transition-colors"
                        data-testid="sa-campus-video-yt-shortcut">
                    <Youtube size={12} /> YouTube/Vimeo
                </button>
            )}
            <label className="cursor-pointer">
                <input type="file" className="hidden" accept={meta.accept}
                       onChange={(e) => onUpload(e.target.files?.[0])}
                       data-testid={`sa-campus-${kind}-file`} />
                <span className="inline-flex items-center px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-xs hover:bg-primary-hover">
                    {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                </span>
            </label>
            {url && <Button size="sm" variant="ghost" className="text-red-500" onClick={onClear}><X size={12} /></Button>}
        </div>
    );
}
